import React, {createRef, ReactNode, RefObject} from "react";
import {Button} from "primereact/button";
import {Menu} from "primereact/menu";
import {DropdownChangeEvent, DropdownProps} from "primereact/dropdown";
import {MenuItem} from "primereact/menuitem";
import FunctionHelper from "../helper/FunctionHelper";
import {InputText} from "primereact/inputtext";
import DomHelper from "../helper/DomHelper";
import Translate from "../translation/Translate";
import PortalPureComponent from "./PortalPureComponent";

/**
 * The properties for a {@link DropdownFilter} component
 */
interface DropdownFilterProperties extends DropdownProps {
	onClear?: () => void;
}

/**
 * The state for a {@link DropdownFilter} component
 */
interface DropdownFilterState extends DropdownProps {
	searchValue?: string;
}

/**
 * This component is a filterable dropdown menu. It combines an {@link InputText} as search input with a {@link Menu}
 * appended to a {@link Button} to make this often used combination reusable.
 */
export default class DropdownFilter extends PortalPureComponent<DropdownFilterProperties, DropdownFilterState> {
	constructor(props: Readonly<DropdownFilterProperties>) {
		super(props);

		this.state = {};
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<DropdownFilterProperties>, prevState: Readonly<DropdownFilterState>, snapshot?: any
	): Promise<void> {
		if (this.props.value !== prevProps.value) {
			this.setState({
				searchValue: undefined
			});
		}
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let id: string = FunctionHelper.applyValue(this.props.id, DomHelper.generateUUID());
		let menu: RefObject<Menu> = createRef<Menu>();
		let menuItems: Array<MenuItem> = [];

		for (let item of FunctionHelper.applyValue(this.props.options, [])) {
			if (FunctionHelper.exists(this.state.searchValue) &&
				item.label.toLowerCase().search(this.state.searchValue?.toLowerCase()) === -1) {
				continue;
			}

			menuItems.push({
				label: item.label,
				command: (e) => {
					if (!FunctionHelper.exists(this.props.onChange)) {
						return;
					}

					this.setState({
						searchValue: undefined
					}, () => {
						this.props.onChange!({
							value: item.value,
							originalEvent: e.originalEvent
						} as DropdownChangeEvent);
					});
				}
			});
		}

		if (menuItems.length === 0) {
			menuItems.push({
				label: Translate.value("inputs.dropdown_filter.empty"),
				disabled: true
			});
		}

		return (
			<div className="p-inputgroup dropdown-filter">
				<InputText value={FunctionHelper.applyValue(this.state.searchValue, this.props.value)} id={id}
						   onChange={(event) => {
							   this.setState({
								   searchValue: event.target.value
							   });
							   menu.current?.show(event);
						   }}/>
				<Button icon="icon icon-close" tooltip={Translate.value("inputs.dropdown_filter.clear")}
						onClick={() => {
							this.setState({
								searchValue: undefined
							});

							if (FunctionHelper.exists(this.props.onClear)) {
								this.props.onClear!();
							}
						}}/>
				<Button icon="icon icon-angle-down" aria-controls={id + "-menu"} aria-haspopup={true}
						onClick={(event) => menu.current!.toggle(event)}/>
				<Menu popup={true} ref={menu} id={id + "-menu"} appendTo={this.props.appendTo} model={menuItems}/>
			</div>
		);
	}
}